import { Inject, Injectable, Optional } from "@angular/core";
import { DecodedTokenInterface, JwtHelper, LocalstorageHelper, LogService, SerializabledDataType, TenantType, TokenType } from "@nts/std/utility";
import { distinctUntilChanged, Subject } from "rxjs";
import { AUTH_MOCK_MODE, AuthStorageKeys } from "./auth.const";
import { EnterpriseDataDto } from "../..";

@Injectable({
    providedIn: 'root'
})
export class AuthenticationTokenService {

    private onAccessTokenRetrieved = new Subject<string>();

    /**
     * Recupera l'enterprise corrente salvata nella session per il tenant corrente
     *
     * @param tenantId
     * @returns
     */
    async getEnterpriseData(tenantId: number): Promise<EnterpriseDataDto> {
        if (tenantId == null || tenantId === 0) {
            return null;
        }
        try {
            return JSON.parse(window.sessionStorage.getItem(`enterpriseData_${tenantId}`));
        } catch (err) {
            return null;
        }
    }

    /**
     * 
     * @param accessToken 
     * @returns 
     */
    async getTenantId(accessToken: string = null): Promise<number> {
        accessToken = accessToken ?? await this.getAccessToken();
        const parsedAccessToken = this.decodeToken(accessToken);
        return parseInt(parsedAccessToken?.Tnt, 10) || 0;
    }

    async getUserName(accessToken: string = null): Promise<string> {
        accessToken = accessToken ?? await this.getAccessToken();
        const parsedAccessToken = this.decodeToken(accessToken);
        return parsedAccessToken?.family_name + ' ' + parsedAccessToken?.given_name;
    }

    async getUserId(accessToken: string = null): Promise<number> {
        accessToken = accessToken ?? await this.getAccessToken();
        const parsedAccessToken = this.decodeToken(accessToken);
        return parseInt(parsedAccessToken?.sub, 10) || 0;
    }

    async tokenHasTenantId(): Promise<boolean> {
        const accessToken = await this.getAccessToken();
        if (accessToken) {
            const parsedAccessToken = this.decodeToken(accessToken);
            return parsedAccessToken?.Tnt != null;
        }
        return false;
    }

    async storeAccessToken(newAccessToken: string): Promise<boolean> {
        return await LocalstorageHelper.setStorageItem(AuthStorageKeys.ACCESS_TOKEN_KEY, newAccessToken, undefined, false, false);
    }

    async setAccessToken(newAccessToken: string): Promise<void> {
        if (newAccessToken != null) {
            await this.onAccessTokenBeforeChanged(newAccessToken);
            await this.storeAccessToken(newAccessToken);
            LocalstorageHelper.setCurrentUserId(await this.getCurrentUserId(newAccessToken));
        }
    }

    async retrieveRefreshToken(): Promise<SerializabledDataType> {
        return await LocalstorageHelper.getStorageItem(AuthStorageKeys.REFRESH_TOKEN_KEY, undefined, false, false);
    }

    async retrieveTenantProfile(): Promise<Record<string, any>> {
        return await LocalstorageHelper.getStorageItem(AuthStorageKeys.TENANT_PROFILE_KEY, undefined, undefined, false) as Record<string, any>;
    }

    async setEnterpriseData(enterpriseData: EnterpriseDataDto, tenantId: number): Promise<void> {
        window.sessionStorage.setItem(`enterpriseData_${tenantId}`, JSON.stringify(enterpriseData));
        LocalstorageHelper.setCurrentEnterpriseId(enterpriseData.enterpriseId);
        await LocalstorageHelper.setStorageItem(AuthStorageKeys.LAST_USED_ENTERPRISE_DATA_KEY, enterpriseData, undefined, undefined, false);
    }

    async clearEnterpriseData(tenantId: number): Promise<void> {
        window.sessionStorage.removeItem(`enterpriseData_${tenantId}`);
        LocalstorageHelper.setCurrentEnterpriseId(0);
        await LocalstorageHelper.removeStorageItem(AuthStorageKeys.LAST_USED_ENTERPRISE_DATA_KEY, undefined, undefined, false);
    }

    async storeUserProfile(userProfile: Record<string, any>) {
        await LocalstorageHelper.setStorageItem(AuthStorageKeys.USER_PROFILE_KEY, userProfile, undefined, false, false);
    }

    async retrieveUserProfile(): Promise<Record<string, any>> {
        return await LocalstorageHelper.getStorageItem(AuthStorageKeys.USER_PROFILE_KEY, undefined, false, false) as Record<string, any>;
    }

    async storeTenantProfile(tenantProfile: Record<string, any>): Promise<boolean> {
        return await LocalstorageHelper.setStorageItem(AuthStorageKeys.TENANT_PROFILE_KEY, tenantProfile, undefined, undefined, false);
    }

    async storeRefreshToken(newRefreshToken: string): Promise<boolean> {
        return LocalstorageHelper.setStorageItem(AuthStorageKeys.REFRESH_TOKEN_KEY, newRefreshToken, undefined, false, false);
    }

    getTokenExpireInMinute(token: DecodedTokenInterface): number {
        const now = new Date();
        const expMinutesToken = (((token.exp * 1000) - now.getTime()) / 60000);
        return expMinutesToken;
    }

    isTokenValid(token: string): boolean {
        if (token === '' || token == null || !JwtHelper.verifyHeader(token)) {
            return false;
        } else {
            const expTime = this.getExpirationTime(token);
            return expTime ? expTime > new Date() : false;
        }
    }

    private getExpirationTime(token: string): Date | boolean {
        const parsedToken = this.decodeToken(token);
        return parsedToken ? new Date((parsedToken.exp * 1000)) : false;
    }

    private async onAccessTokenBeforeChanged(newAccessToken: string) {
        // Se il token è valido aggiorno il localstoragehelper
        if (newAccessToken?.length > 0 && this.checkTokenType(newAccessToken, TokenType.LoginSucceeded)) {
            if (this.mockMode) {
                LocalstorageHelper.setCurrentTenantId(1);
            } else {
                const decodedToken = this.decodeToken(newAccessToken);
                if (decodedToken) {
                    LocalstorageHelper.setCurrentTenantId(parseInt(decodedToken?.Tnt));
                }
            }
        }
    }

    decodeToken(token: string): DecodedTokenInterface | null {
        if (!token || token?.length === 0) {
            return null;
        }
        let decoded: DecodedTokenInterface = null;
        try {
            decoded = JwtHelper.decodeToken(token);
        } catch (e) {
            LogService.warn(e);
        }
        return decoded;
    }

    /**
     * Recupera l'access tokene presente nello storage.
     * Dopo averlo recuperato emette il Subject onAccessTokenRetrieved
     *
     * @returns l'access token presente nello storage
     */
    async getAccessToken(): Promise<string> {
        const token = await this.retrieveAccessToken() as string;
        if (token) {
            LocalstorageHelper.setCurrentUserId(await this.getCurrentUserId(token));
        }
        this.onAccessTokenRetrieved.next(token);
        return token;
    }

    async getCurrentUserId(token?: string): Promise<number> {
        if (this.mockMode) {
            return 1;
        }
        token = token || await this.getAccessToken();
        const parsedToken = this.decodeToken(token);
        return parsedToken ? parseInt(parsedToken.sub, 10) : 0;
    }

    /**
     * Recupera l'access token
     * @returns access token
     */
    async retrieveAccessToken(): Promise<SerializabledDataType> {
        return await LocalstorageHelper.getStorageItem(AuthStorageKeys.ACCESS_TOKEN_KEY, undefined, false, false);
    }

    async clearAuthData(): Promise<void> {
        await LocalstorageHelper.removeStorageItem(AuthStorageKeys.ACCESS_TOKEN_KEY, undefined, false, false);
        await LocalstorageHelper.removeStorageItem(AuthStorageKeys.REFRESH_TOKEN_KEY, undefined, false, false);
        await LocalstorageHelper.removeStorageItem(AuthStorageKeys.USER_PROFILE_KEY, undefined, false, false);
        await LocalstorageHelper.removeStorageItem(AuthStorageKeys.TENANT_PROFILE_KEY);
        LogService.clearProfiles();
    }

    checkTokenTenantType(token: string, tenantType: TenantType): boolean {
        const parsedToken = this.decodeToken(token);
        return parsedToken ? parsedToken.TntT === tenantType : false;
    }

    checkTokenType(token: string, tokenType: TokenType): boolean {
        if (this.mockMode) {
            return true;
        }
        const parsedToken: DecodedTokenInterface = this.decodeToken(token);
        return parsedToken ? parsedToken.TkT === tokenType : false;
    }

    constructor(
        @Optional()
        @Inject(AUTH_MOCK_MODE) private readonly mockMode: boolean
    ) {
        this.onAccessTokenRetrieved
            .pipe(distinctUntilChanged())
            .subscribe((accessToken: string) => {
                // Se il token è valido aggiorno il localstoragehelper
                if (accessToken?.length > 0 && this.checkTokenType(accessToken, TokenType.LoginSucceeded)) {
                    if (this.mockMode) {
                        LocalstorageHelper.setCurrentTenantId(1);
                    } else {
                        const decodedToken = this.decodeToken(accessToken);
                        if (decodedToken) {
                            LocalstorageHelper.setCurrentTenantId(parseInt(decodedToken?.Tnt));
                        }
                    }
                }
            });
    }
}